import {
  useCallback, useEffect, useRef
} from 'react';
import { connect } from 'react-redux';
import NProgress from 'nprogress';
import PropTypes from 'prop-types';
import Router from 'next/router';
import { Analytics } from '@vercel/analytics/react';
import {
  SIGN_IN_KEY,
  PRIVACY_POLICY_KEY,
  UPDATE_STORIES_KEY,
  APP_DOWNLOAD_KEY,
  APP_DOWNLOAD_PARTNER_PROGRAM_KEY
} from '@/config/routeKeys';
import BottomNavigationBar from '@/components/app/BottomNavigationBar';
import HeaderBarResponsive from '@/components/app/Headers/HeaderBarResponsive';
import { consoleLogInformation } from '@/helpers/logging';
import { getRouteObject } from '@/helpers/routing';
import { isBrowser } from '@/helpers/browser';
import { selectShowRouteLoader } from '@/state/selectors/routes';
import {
  setRouteLoading,
  setRouteLoadingComplete,
  showRouteLoader,
  updateRouterPath
} from '@/state/router/actions';

const App = ({
  showLoadingSpinner,
  Component: ComponentProp,
  pageProps,
  router,
  dispatch
}) => {
  const routeLoaderTimeoutRef = useRef();

  useEffect(() => {
    // Log information about the app to the console
    // such as NODE_ENV and browser information
    consoleLogInformation();
  }, []);

  useEffect(() => {
    // Nprogress is the circular loading spinner at the top right hand of the page
    // Enable or disable this as and when needed
    if (showLoadingSpinner) {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [showLoadingSpinner]);

  // Called when a route change begins
  const routeChangeStart = useCallback(() => {
    // Mark the route change as loading`
    dispatch(setRouteLoading());
    // Instruct the visual loader to display if loading takes more than 1 second
    clearTimeout(routeLoaderTimeoutRef.current);
    routeLoaderTimeoutRef.current = setTimeout(() => dispatch(showRouteLoader()), 1000);
  }, [dispatch]);

  // Called before the browser history is updated.
  // Saves the previous route in the redux store for reference when navigating
  const beforeHistoryChange = useCallback(() => {
    if (isBrowser()) {
      dispatch(updateRouterPath(document.location.pathname));
    }
  }, [dispatch]);

  // Called after a route change happens successfully.
  const routeChangeComplete = useCallback(() => {
    setTimeout(() => {
      // Stop showing the visual loader
      dispatch(showRouteLoader(false));
      // Clear any pending loader instructions
      clearTimeout(routeLoaderTimeoutRef.current);
      // Mark the route change as complete
      dispatch(setRouteLoadingComplete());
    }, 0);
  }, [dispatch]);

  // Called after a route change errors.
  const routeChangeError = useCallback(() => {
    routeChangeComplete();
  }, [routeChangeComplete]);

  useEffect(() => {
    // Add event listeners to the router
    Router.events.on('routeChangeStart', routeChangeStart);
    Router.events.on('beforeHistoryChange', beforeHistoryChange);
    Router.events.on('routeChangeComplete', routeChangeComplete);
    Router.events.on('routeChangeError', routeChangeError);

    return () => {
      // Remove event listeners from the router
      Router.events.off('routeChangeStart', routeChangeStart);
      Router.events.off('beforeHistoryChange', beforeHistoryChange);
      Router.events.off('routeChangeComplete', routeChangeComplete);
      Router.events.off('routeChangeError', routeChangeError);
    };
  }, [routeChangeStart, routeChangeComplete, routeChangeError, beforeHistoryChange]);

  const routeKey = (getRouteObject(router.asPath) || {}).key;
  const displayHeaderAndFooter = (routeKey !== UPDATE_STORIES_KEY)
    && (routeKey !== SIGN_IN_KEY)
    && (routeKey !== PRIVACY_POLICY_KEY)
    && (routeKey !== APP_DOWNLOAD_KEY)
    && (routeKey !== APP_DOWNLOAD_PARTNER_PROGRAM_KEY);
    
  return (
    <div
      className='app'
      id='app-root'
    >
      { displayHeaderAndFooter && <HeaderBarResponsive /> }
      <ComponentProp { ...pageProps } />
      <Analytics />
      { displayHeaderAndFooter && <BottomNavigationBar /> }
    </div>
  );
};

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  hasError: PropTypes.bool,
  showLoadingSpinner: PropTypes.bool
};

App.defaultProps = {
  hasError: false,
  showLoadingSpinner: false
};

// ------ Connect the page with the store and inject required state as props ------
const mapStateToProps = (state) => ({ showLoadingSpinner: selectShowRouteLoader(state) });

export default connect(mapStateToProps)(App);